import type { ProductTypeEnum } from '@ztechapps/common.contract'

/**
 * Vendor entry payload used by the PO-side `Vendor` product path. Mirrors
 * the `ProductVendorEntryInput` shape on `BuildProductFromDraftArgs`.
 */
export type ProductDraftVendorEntry = {
  vendorId: string
  vendorCode: string
  unitPrice: number
}

/**
 * The mutable subset of the proposal — what the admin can override before
 * commit. Everything is optional because the parent merges the patch over
 * the original draft.
 */
export type ProductDraft = {
  code?: string
  name?: string
  productType?: ProductTypeEnum
  shape?: string
  verticalIn?: number
  horizontalIn?: number
  cornerRadiusIn?: number
  price?: number
  baseProductId?: string | null
  baseProductCode?: string | null
  vendors?: ProductDraftVendorEntry[]
  notes?: string
  tag?: string
  activityPrefix?: string
  tags?: string[]
  remarksActivity?: { kind: string; message: string }
}

/**
 * Snapshot of an already-existing product matching the proposed code.
 * Present only when the backend duplicate check fired.
 */
export type ProductDuplicateSnapshot = {
  _id: string
  code: string
  name?: string
  coverImage?: string
}

/**
 * Source of the clone-base resolution. Wire format matches the backend
 * `resolution.source` tag in the proposal payload.
 *
 *   - `Auto`     — backend Euclidean re-rank picked the nearest match.
 *   - `Override` — admin (or LLM) supplied `baseProductId` directly.
 *   - `None`     — no candidate within the cap; admin must pick manually.
 *   - `Scratch`  — no clone base at all (PO-side Vendor path always).
 */
export enum ProductProposalResolutionSourceEnum {
  Auto = 'auto',
  Override = 'override',
  None = 'none',
  Scratch = 'scratch',
}

/**
 * Clone-base resolution metadata. Drives the "Cloned from…" footnote and
 * the manual-override badge.
 *
 * `distance` is mm-Euclidean on `(vertical, horizontal)`; absent when
 * `source === Scratch` or when no base was matched.
 */
export type ProductProposalResolution = {
  source: ProductProposalResolutionSourceEnum
  distance?: number
  warning?: string
}

/**
 * Where the proposal originated. Wire format matches the backend `origin`
 * tag in the proposal payload — drives mode-aware copy in the card
 * (standalone chat vs PO inline).
 */
export enum ProductProposalOriginEnum {
  CreateProductChat = 'create-product-chat',
  CreatePurchaseOrderInline = 'create-purchase-order-inline',
}

/**
 * Whether the proposal is committing a new product or pointing the admin
 * at an already-existing one.
 */
export enum ProductProposalActionEnum {
  Create = 'create',
  OpenExisting = 'open-existing',
}

/**
 * Full product-proposal payload as decoded from the
 * `application/x-papi-product-proposal+json` MCP resource. The `draft`
 * block matches `BuildProductFromDraftArgs` field-for-field so the commit
 * hook can map it directly to the engine.
 *
 * `ref` is set only on PO-side inline proposals — it's the temp marker
 * the PO draft's line items reference via `inlineProposalRef`. The Web UI
 * swaps both for the engine's real `_id` after commit.
 */
export type ProductProposal = {
  ref?: string
  origin: ProductProposalOriginEnum
  action?: ProductProposalActionEnum
  tag?: string
  activityPrefix?: string
  draft: ProductDraft & {
    code: string
    name: string
    productType: ProductTypeEnum
  }
  resolution: ProductProposalResolution
  duplicate?: ProductDuplicateSnapshot
}
